<script>
import Vue from 'vue'
import html2canvas from 'html2canvas'
import CardInfoBLCapture from './CardInfoBLCapture.vue'

export default {
    name: "CardInfoBL",
    props: {
        dossiers: {
            type: Array,
            required: true
        }
    },
    methods: {
        formatNumber(value) {
            if (value == null) return "";
            return new Intl.NumberFormat("fr-FR").format(value);
        },
        getQteClass(qte) {
            // Retourne la classe CSS basée sur la quantité
            return qte > 0 ? 'qte-restant' : 'qte-cloturee';
        },
        getStatusClass(dossier) {
            // etat_dossier > 0 => Clôturé
            if (dossier.etat_dossier > 0) return 'is_green';

            // etat_dossier <= 0 :
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dateArrivee = new Date(dossier.ouverture_date_arriver);

            // date_arrivee_navire > date_actuelle => Non clôturé (à venir)
            if (dateArrivee > today) return 'is_pending';

            // date_arrivee_navire <= date_actuelle => En cours
            return 'is_en_cours';
        },
        getPlusInfo(dossier) {
            return (dossier.dossier_info_plus && dossier.dossier_info_plus.length > 0)
                ? dossier.dossier_info_plus[0]
                : {};
        },
        getStatutClass(statut) {
            return statut ? 'statut-badge-ok' : 'statut-badge-vide';
        },
        async captureCard(dossier, index) {
            // Rendu hors-ecran d'un composant dedie, large et non responsive,
            // pour eviter le retrecissement/chevauchement du texte qui se
            // produisait en capturant la carte affichee dans la grille
            // (largeur mini 300px).
            const CaptureComponent = Vue.extend(CardInfoBLCapture);
            const instance = new CaptureComponent({ propsData: { dossier } });
            instance.$mount();

            instance.$el.style.position = 'fixed';
            instance.$el.style.top = '0';
            instance.$el.style.left = '-9999px';
            document.body.appendChild(instance.$el);

            try {
                await this.$nextTick();
                const canvas = await html2canvas(instance.$el, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    useCORS: true,
                    // foreignObjectRendering (via <foreignObject> SVG) est experimental
                    // et produit des captures noires/vides avec ce rendu (border-radius +
                    // overflow:hidden + degrade). Le rendu "canvas" par defaut est plus fiable.
                    foreignObjectRendering: false
                });
                const link = document.createElement('a');
                link.download = `dossier_${dossier.ouverture_bl_numero || dossier.numero_repertoire || index}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
            } catch (e) {
                console.error(e);
                Vue.$toast.open({
                    message: "Impossible de capturer la carte",
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                document.body.removeChild(instance.$el);
                instance.$destroy();
            }
        }
    }
}
</script>

<template>
    <div  class="dossier-grid">
        <div class="dossier-card"
             v-for="(dossier, index) in dossiers" :key="index"
             :class="getStatusClass(dossier)"
        >
            <div class="card-top-actions">
                <div class="status-badge">
                    REP : {{dossier.numero_repertoire}}
                </div>
                <button
                    type="button"
                    class="capture-btn"
                    data-html2canvas-ignore="true"
                    title="Capturer la carte en image"
                    @click="captureCard(dossier, index)"
                >
                    <i class="fas fa-camera"></i>
                </button>
            </div>
            <div class="dossier-number">
                {{ dossier.nom_client }}
            </div>

            <div class="dossier-info">
                <div class="dossier-details">
                    <div class="detail-row">
                        <span class="detail-label text-uppercase">Numero BL :</span>
                        <span class="detail-value">{{ dossier.ouverture_bl_numero }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">NUMÉRO DE FACTURE :</span>
                        <span class="detail-value"> {{dossier.ouverture_invoice}} </span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">DATE DE RÉCEPTION DES DOCUMENTS :</span>
                        <span class="detail-value"> {{ dossier.ouverture_date_dossier_formatted }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">DATE D'ARRIVÉE PRÉVUE DU NAVIRE :</span>
                        <span class="detail-value"> {{ dossier.ouverture_date_arriver_formatted }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">NOM DU NAVIRE :</span>
                        <span class="detail-value"> {{ dossier.ouverture_navire }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">MARCHANDISES :</span>
                        <span class="detail-value"> {{ dossier.ouverture_articles }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">POIDS :</span>
                        <span class="detail-value"> {{ dossier.ouverture_poids_net }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">QUANTITÉ DE CONTENEURS (Qté TC) :</span>
                        <span class="detail-value text-bold">{{ dossier.total_tc }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">TYPE:</span>
                        <span class="detail-value text-bold">{{ dossier.type_container }}</span>
                    </div>


                    <div class="detail-row">
                        <span class="detail-label text-uppercase">Franchise MICTSL:</span>
                        <span class="detail-value text-bold">{{ getPlusInfo(dossier).franchise_mictsl }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label text-uppercase">Date limite mictsl:</span>
                        <span class="detail-value text-bold">{{ getPlusInfo(dossier).date_limit_mictsl_formatted }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label text-uppercase">Franchise Compagnie:</span>
                        <span class="detail-value text-bold">{{ getPlusInfo(dossier).franchise_compagnie }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label text-uppercase">Date Limite:</span>
                        <span class="detail-value text-bold">{{ getPlusInfo(dossier).date_limite_2_formatted }}</span>
                    </div>
                </div>
            </div>

            <div :class="dossier.articles.length > 0 ? 'list_article' : 'list_article_not_border'">
                <div v-for="(article, index_article) in dossier.articles" :key="index_article">
                    <div class="article-label">
                        {{ article.reference_article }}
                    </div>

                    <div :class="getQteClass(article.qte_restant)">
                        <i :class="article.qte_restant == 0 ? 'fa fa-check-circle' : ''"></i>
                        {{ article.qte_restant !=0 ? formatNumber(article.qte_restant) : '' }}
                    </div>
                </div>
            </div>

            <div :class="dossier.document_associer.length > 0 ? 'list_article' : 'list_article_not_border'">
<!--                <div v-for="(article, index_article) in dossier.document_associer" :key="index_article">
                    <div class="article-label">
                        {{ article.reference_article }}
                    </div>

                    <div :class="getQteClass(article.qte_restant)">
                        <i :class="article.qte_restant == 0 ? 'fa fa-check-circle' : ''"></i>
                        {{ article.qte_restant !=0 ? formatNumber(article.qte_restant) : '' }}
                    </div>
                </div>-->
                <table class="table table-striped table-bordered">
                    <thead >
                        <tr>
                            <th>Document</th>
                            <th>Statut</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(doc, index) in dossier.document_associer" :key="index">
                            <td> {{doc.nom_document}}</td>
                            <td>
                                <span class="statut-badge" :class="getStatutClass(doc.statut)">
                                    <i :class="doc.statut ? 'fa fa-check-circle' : 'fa fa-exclamation-circle'"></i>
                                    {{ doc.statut ? doc.statut : 'Manquant' }}
                                </span>
                            </td>
                            <td>{{doc.date_reception_formatted}}</td>
                        </tr>
                    </tbody>
                </table>
            </div>


        </div>
    </div>
</template>

<style scoped>
/* --- Structure de la Grille --- */
.dossier-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    padding: 10px;
}

/* --- Style de la Carte --- */
.dossier-card {
    background: white;
    border-radius: 15px;
    padding: 20px;
    transition: all 0.3s ease;
    box-shadow: 0 5px 15px rgba(0,0,0,0.08);
    cursor: pointer;
    border: 1px solid #e9ecef;
    position: relative;
    overflow: hidden;
}

.dossier-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #076360, #0a7a77);
}



.dossier-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.15);
    border-color: #076360;
}

/* --- Bandeau d'actions (statut + capture) en haut de carte --- */
.card-top-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

/* --- Bouton Capture d'écran --- */
.capture-btn {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: #076360;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;
}

.capture-btn i {
    font-size: 13px;
}

.capture-btn:hover {
    background: #0a7a77;
    transform: scale(1.1);
}

/* --- Badge de Statut/Date --- */
.status-badge {
    padding: 4px 10px;
    border-radius: 15px;
    font-size: 14px;
    color: #3a2626;
    background-color: #f8f9fa;
    font-weight: bold !important;
    white-space: nowrap;
}

/* --- Informations Générales --- */
.dossier-number {
    font-size: 18px;
    font-weight: 700;
    color: #076360;
    margin-bottom: 8px;
}

.dossier-details {
    border-top: 1px solid #f0f0f0;
    padding-top: 15px;
    margin-top: 15px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 14px;
}

.detail-label {
    color: #6c757d;
    font-weight: 600;
}

.detail-value {
    color: #212529;
    font-weight: 500;
}

.text-bold {
    font-weight: 700 !important;
}

/* --- LISTE DES ARTICLES --- */
.list_article_not_border {
    padding-top: 5px;
    margin-top: 5px;
}

.list_article {
    border-top: 2px solid #e9ecef; /* Séparateur */
    padding-top: 5px;
    margin-top: 5px;
}

.list_article > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 3px 0;
    font-size: 14px;
}

.article-label {
    color: #343a40;
    font-weight: 500;
    flex-grow: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 15px;
}

/* --- Styles Quantité Conditionnels --- */

.qte-restant, .qte-cloturee {
    /* Styles de base communs */
    font-weight: 700;
    min-width: 40px;
    text-align: right;
    padding: 2px 8px;
    border-radius: 4px;
    white-space: nowrap;
}

/* Style pour QTE > 0 (Reste à livrer / Attention) */
.qte-restant {
    color: #dc3545; /* Texte Rouge */
    background-color: #f8d7da; /* Fond Rouge Léger */
    border: 1px solid #f5c6cb;
}
.qte-restant .fa {
    color: #dc3545; /* Icône en Rouge */
    margin-right: 5px;
}


/* Style pour QTE = 0 (Clôturé / Succès) */
.qte-cloturee {
    color: #155724; /* Texte Vert Foncé */
    background-color: #d4edda; /* Fond Vert Léger (Success) */
    border: 1px solid #c3e6cb;
}
.qte-cloturee .fa {
    color: #155724; /* Icône en Vert Foncé */
    margin-right: 5px;
}

/* --- Statut Document Associé --- */
.statut-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
}

.statut-badge-ok {
    color: #155724;
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
}
.statut-badge-ok .fa {
    color: #2e7d32;
}

.statut-badge-vide {
    color: #dc3545;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
}
.statut-badge-vide .fa {
    color: #dc3545;
}

/* --- Media Query --- */
@media (max-width: 768px) {
    .dossier-grid {
        grid-template-columns: 1fr;
    }
}

/* --- Dossier en cours (etat_dossier <= 0, date d'arrivée du navire atteinte) --- */
.is_en_cours{
    background: rgba(42, 153, 241, 0.42) !important;
}
.is_en_cours::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
        90deg,
        rgba(42, 153, 241, 1),
        rgba(42, 153, 241, 1)
    ) !important;
}

.is_en_cours .detail-label {
    color: #585a5b !important;
}

.is_green{
    background: #def6e4;
}

/* --- Dossier non clôturé (à venir : date d'arrivée du navire pas encore atteinte) --- */
.is_pending {
    background: #eef1f5;
}
.is_pending::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6c757d, #adb5bd) !important;
}
.is_pending .detail-label {
    color: #6c757d !important;
}

</style>